/**
 * Debug Page
 *
 * Dedicated page for inspecting bond calculation processing at the payment level.
 * Super admin only - allows selection of any candidate and single-candidate processing
 * with detailed debug output.
 */

import { Box, Button, Flex, Heading, HStack, Menu, Text, VStack } from '@chakra-ui/react';
import { ChevronDown, Copy, Download, RefreshCw } from 'lucide-react';
import { type FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PageLayoutGrid } from '~/components/layout/page-utilities/PageLayoutGrid';
import { LAYOUT_SPACING } from '~/components/layout/shared/spacing';
import { toaster } from '~/components/ui/toaster';
import { CandidateProcessor } from '~/engine/processing/managers/candidate-processor';
import { cloneCandidateForReprocessing } from '~/engine/processing/shared/candidate-cloning';
import { useProjectStore } from '~/hooks/useProjectStore';
import {
  DebugCapture,
  DebugExportService,
  type DebugSessionCapture,
  updateCaptureWithResults,
} from '~/services/debug-system';
import type { Candidate, Configuration } from '~/types';
import {
  copyCashFlowTableToClipboard,
  copyTaxYearPlanTableToClipboard,
} from '~/utils/candidate-copy-utilities';
import { convertProjectDataToProject } from '~/utils/project-conversion';
import { CandidateSelectionSidebar } from './CandidateSelectionSidebar';
import { DebugResultsPanel } from './DebugResultsPanel';

// Check if running in development mode
const isDev = import.meta.env.DEV;

/**
 * Debug Page Component
 */
export const DebugPage: FC = () => {
  const navigate = useNavigate();
  const { id: projectId, configId: configurationId } = useParams<{
    id: string;
    configId: string;
  }>();

  // Get project data from store (same pattern as ProposalDevelopmentPage)
  const projectData = useProjectStore((s) => (projectId ? s.projects[projectId] : undefined));
  const configurations = useProjectStore((s) =>
    projectId ? s.projects[projectId]?.configurations || null : null
  );

  // Find the configuration
  const configuration = useMemo((): Configuration | null => {
    if (!configurations || !configurationId) return null;
    return configurations.find((c: Configuration) => c.id === configurationId) ?? null;
  }, [configurations, configurationId]);

  // Get all candidates from the configuration (including failed candidates)
  const candidates = useMemo((): Candidate[] => {
    const viableCandidates = configuration?.candidates || [];
    const failedCandidates = configuration?.failedCandidates || [];
    // Combine both arrays - failed candidates are important for debugging
    return [...viableCandidates, ...failedCandidates];
  }, [configuration?.candidates, configuration?.failedCandidates]);

  // State
  const [selectedCandidateId, setSelectedCandidateId] = useState<string | null>(null);
  const [debugCapture, setDebugCapture] = useState<DebugSessionCapture | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [processedCandidate, setProcessedCandidate] = useState<Candidate | null>(null);

  // Cleanup debug session on unmount or navigation
  useEffect(() => {
    return () => {
      // End any active session when component unmounts
      DebugCapture.endSession();
    };
  }, []);

  // Selected candidate - search in both viable and failed candidates
  const selectedCandidate = useMemo((): Candidate | null => {
    if (!selectedCandidateId || !configuration) return null;
    // First look in viable candidates
    const viableCandidate = configuration.candidates?.find(
      (c: Candidate) => c.id === selectedCandidateId
    );
    if (viableCandidate) return viableCandidate;
    // Then look in failed candidates
    const failedCandidate = configuration.failedCandidates?.find(
      (c: Candidate) => c.id === selectedCandidateId
    );
    return failedCandidate || null;
  }, [selectedCandidateId, configuration]);

  // Reset processed candidate when selecting a new candidate
  useEffect(() => {
    if (!selectedCandidate?.id) return;
    setProcessedCandidate(null);
    setDebugCapture(null);
  }, [selectedCandidate?.id]);

  // Handle running processing for a single candidate
  const handleRunProcessing = useCallback(async () => {
    if (!selectedCandidate || !projectData || !configuration) return;

    setIsProcessing(true);
    setError(null);
    setDebugCapture(null);
    setProcessedCandidate(null);

    // Yield to event loop so React can render the spinner before heavy processing
    await new Promise((resolve) => setTimeout(resolve, 10));

    const startTime = Date.now();

    try {
      // Clone the candidate to avoid mutating the original
      const candidateClone = cloneCandidateForReprocessing(selectedCandidate);

      console.log('[DebugPage] Starting processing', {
        candidateId: candidateClone.id,
        candidateName: candidateClone.name,
        hasMillageInfo: !!candidateClone.millageInformation,
        revenueMapSize: Object.keys(candidateClone.revenueMap || {}).length,
        cashFlowsLength: candidateClone.cashFlows.length,
      });

      // Convert ProjectData to Project type for the CandidateProcessor
      const project = convertProjectDataToProject(projectData, true);

      // Start debug capture session
      DebugCapture.startSession(candidateClone.id, configuration.id, {
        preset: 'full',
        candidateName: candidateClone.name,
        projectId: projectData.metadata.id,
        millageShapeName: selectedCandidate.millageInformation?.millageShape?.shapeName,
      });

      console.log('[DebugPage] Debug capture session started', {
        candidateId: candidateClone.id,
        isCapturing: DebugCapture.isCapturing(candidateClone.id),
      });

      // Process the candidate
      CandidateProcessor.processCandidate(candidateClone, project, configuration);

      console.log('[DebugPage] Processing complete', {
        candidateId: candidateClone.id,
        isProcessed: candidateClone.isProcessed,
        cashFlowsLength: candidateClone.cashFlows.length,
        isViable: candidateClone.results.isViable,
      });

      // Update capture with candidate results (viability, failure messages)
      updateCaptureWithResults(candidateClone);

      // Store the processed candidate for summary display
      setProcessedCandidate(candidateClone);

      // End capture and get results
      const capture = DebugCapture.endSession();
      console.log('[DebugPage] Capture ended', {
        capture: capture
          ? {
              sessionId: capture.sessionId,
              paymentSnapshotsCount: capture.paymentSnapshots?.length || 0,
              summary: capture.summary,
            }
          : null,
      });
      setDebugCapture(capture);

      // Ensure minimum 300ms loading display
      const elapsed = Date.now() - startTime;
      const remainingDelay = Math.max(0, 300 - elapsed);
      if (remainingDelay > 0) {
        await new Promise((resolve) => setTimeout(resolve, remainingDelay));
      }
    } catch (err) {
      console.error('[DebugPage] Processing error', err);
      setError(err instanceof Error ? err.message : 'Processing failed');
      DebugCapture.endSession(); // Clean up capture session
      setProcessedCandidate(null);

      // Ensure minimum 300ms loading display even on error
      const elapsed = Date.now() - startTime;
      const remainingDelay = Math.max(0, 300 - elapsed);
      if (remainingDelay > 0) {
        await new Promise((resolve) => setTimeout(resolve, remainingDelay));
      }
    } finally {
      setIsProcessing(false);
    }
  }, [selectedCandidate, projectData, configuration]);

  // Handle copy cash flow table
  const handleCopyCashFlowTable = useCallback(async () => {
    if (!processedCandidate || !projectData || !configuration) {
      toaster.create({
        title: 'Cannot Copy Cashflow Information',
        description: 'Please run processing first.',
        type: 'warning',
      });
      return;
    }

    try {
      await copyCashFlowTableToClipboard(processedCandidate, projectData, configuration);
      toaster.create({
        title: 'Cashflow Information Copied',
        description: 'Cashflow data has been copied to your clipboard.',
        type: 'success',
        duration: 3000,
      });
    } catch (err) {
      console.error('Failed to copy cashflow information:', err);
      toaster.create({
        title: 'Copy Failed',
        description: err instanceof Error ? err.message : 'Failed to copy cashflow information.',
        type: 'error',
      });
    }
  }, [processedCandidate, projectData, configuration]);

  // Handle copy tax year plan table
  const handleCopyTaxYearPlanTable = useCallback(async () => {
    if (!processedCandidate || !projectData || !configuration) {
      toaster.create({
        title: 'Cannot Copy Tax Year Plan Information',
        description: 'Please run processing first.',
        type: 'warning',
      });
      return;
    }

    try {
      await copyTaxYearPlanTableToClipboard(processedCandidate, projectData, configuration);
      toaster.create({
        title: 'Tax Year Plan Information Copied',
        description: 'Tax year plan data has been copied to your clipboard.',
        type: 'success',
        duration: 3000,
      });
    } catch (err) {
      console.error('Failed to copy tax year plan information:', err);
      toaster.create({
        title: 'Copy Failed',
        description:
          err instanceof Error ? err.message : 'Failed to copy tax year plan information.',
        type: 'error',
      });
    }
  }, [processedCandidate, projectData, configuration]);

  // Handle export to JSON
  const handleExportJSON = useCallback(() => {
    if (!debugCapture) return;
    try {
      DebugExportService.downloadAsJSON(debugCapture);
      toaster.create({
        title: 'Export Complete',
        description: 'Debug data exported to JSON file.',
        type: 'success',
        duration: 3000,
      });
    } catch (err) {
      toaster.create({
        title: 'Export Failed',
        description: err instanceof Error ? err.message : 'Failed to export debug data.',
        type: 'error',
      });
    }
  }, [debugCapture]);

  // Generate text to copy for debug capture
  const getDebugCaptureText = useCallback(() => {
    if (!debugCapture) return '';
    const tsv = DebugExportService.exportToTSV(debugCapture);
    const tsvHeaderRow = tsv.split('\n')[0];
    const reversedTransactions = tsv.split('\n').slice(1).reverse().join('\n');
    return `${tsvHeaderRow}\n${reversedTransactions}`;
  }, [debugCapture]);

  // Handle export cashflows (dev only) - exports raw cashflow data for LLM analysis
  const handleExportCashflows = useCallback(() => {
    if (!selectedCandidate || !configuration) return;
    try {
      DebugExportService.downloadCashflowsAsJSON(selectedCandidate, configuration);
      toaster.create({
        title: 'Cashflows Exported',
        description: 'Save to apps/web/debug-output/ for LLM access.',
        type: 'success',
        duration: 5000,
      });
    } catch (err) {
      toaster.create({
        title: 'Export Failed',
        description: err instanceof Error ? err.message : 'Failed to export cashflows.',
        type: 'error',
      });
    }
  }, [selectedCandidate, configuration]);

  // Handle back navigation
  const handleBack = useCallback(() => {
    navigate(`/projects/${projectId}/proposal`, { state: { fromDebug: true } });
  }, [navigate, projectId]);

  // Loading state
  if (!projectData || !configuration) {
    return (
      <Box p={LAYOUT_SPACING.PAGE_PADDING}>
        <Text>Loading...</Text>
      </Box>
    );
  }

  return (
    <PageLayoutGrid
      leftSidebar={
        <CandidateSelectionSidebar
          candidates={candidates}
          selectedId={selectedCandidateId}
          onSelect={setSelectedCandidateId}
          onRunProcessing={handleRunProcessing}
          isProcessing={isProcessing}
        />
      }
    >
      <VStack align="stretch" gap={LAYOUT_SPACING.SECTION_GAP} p={LAYOUT_SPACING.PAGE_PADDING}>
        {/* Header */}
        <Flex justify="space-between" align="center">
          <VStack align="start" gap={1}>
            <Heading size="lg">Debug Inspector</Heading>
            <Text color="fg.muted" fontSize="sm">
              Configuration: {configuration.name || 'Unnamed'} | Project:{' '}
              {projectData.metadata.name}
            </Text>
          </VStack>
          <HStack gap={3}>
            {/* Copy to Clipboard Dropdown */}
            {(debugCapture || (processedCandidate && !isProcessing)) && (
              <Menu.Root positioning={{ placement: 'bottom-end' }} closeOnSelect>
                <Menu.Trigger asChild>
                  <Button size="sm" variant="outline">
                    <HStack gap={2} w="100%">
                      <Copy size={16} />
                      <Text flex={1}>Copy Data</Text>
                      <ChevronDown size={16} />
                    </HStack>
                  </Button>
                </Menu.Trigger>
                <Menu.Positioner>
                  <Menu.Content minW="320px" maxW="400px" w="100%">
                    {debugCapture && (
                      <Menu.Item
                        value="debug-tsv"
                        w="100%"
                        onClick={async () => {
                          const text = await getDebugCaptureText();
                          await navigator.clipboard.writeText(text);
                          toaster.create({
                            title: 'Copied',
                            description: 'Payment-by-Payment Debug (TSV) copied to clipboard.',
                            type: 'success',
                            duration: 3000,
                          });
                        }}
                      >
                        Payment-by-Payment Debug (TSV)
                      </Menu.Item>
                    )}
                    {processedCandidate && !isProcessing && (
                      <>
                        <Menu.Item value="cashflow" w="100%" onClick={handleCopyCashFlowTable}>
                          Final Cash Flow Table
                        </Menu.Item>
                        <Menu.Item value="taxyear" w="100%" onClick={handleCopyTaxYearPlanTable}>
                          Final Tax Year Plan Table
                        </Menu.Item>
                      </>
                    )}
                  </Menu.Content>
                </Menu.Positioner>
              </Menu.Root>
            )}

            {/* Export Dropdown */}
            {(debugCapture || (isDev && selectedCandidate)) && (
              <Menu.Root positioning={{ placement: 'bottom-end' }} closeOnSelect>
                <Menu.Trigger asChild>
                  <Button size="sm" variant="solid">
                    <HStack gap={2} w="100%">
                      <Download size={16} />
                      <Text flex={1}>Export Data</Text>
                      <ChevronDown size={16} />
                    </HStack>
                  </Button>
                </Menu.Trigger>
                <Menu.Positioner>
                  <Menu.Content minW="320px" maxW="400px" w="100%">
                    {debugCapture && (
                      <Menu.Item value="debug-json" w="100%" onClick={handleExportJSON}>
                        Payment-by-Payment Debug (JSON)
                      </Menu.Item>
                    )}
                    {isDev && selectedCandidate && (
                      <Menu.Item value="raw-cashflows" w="100%" onClick={handleExportCashflows}>
                        Raw Cashflows (JSON)
                      </Menu.Item>
                    )}
                  </Menu.Content>
                </Menu.Positioner>
              </Menu.Root>
            )}

            <Button size="sm" colorPalette="green" onClick={handleBack}>
              Back to Proposal
            </Button>
          </HStack>
        </Flex>

        {/* Main content */}
        <DebugResultsPanel
          capture={debugCapture}
          isProcessing={isProcessing}
          error={error}
          candidateName={selectedCandidate?.name}
          processedCandidate={processedCandidate}
        />

        {/* Instructions when no candidate selected */}
        {!selectedCandidateId && !isProcessing && (
          <Box
            p={8}
            bg="bg.subtle"
            borderRadius="md"
            borderWidth="1px"
            borderStyle="dashed"
            textAlign="center"
          >
            <RefreshCw size={48} style={{ margin: '0 auto 16px', opacity: 0.5 }} />
            <Heading size="md" mb={2}>
              Select a Candidate
            </Heading>
            <Text color="fg.muted">
              Choose a candidate from the sidebar and click &quot;Run Processing&quot; to capture
              debug state.
            </Text>
          </Box>
        )}
      </VStack>
    </PageLayoutGrid>
  );
};

export default DebugPage;
